import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../api"; // axios instance (token + school_id auto set থাকে)
import Select from "react-select"; // dropdown এর জন্য react-select

const ClassSubjectAssignPage = () => {
    const [form, setForm] = useState({ class_id: "", subject_ids: [] });
    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [editId, setEditId] = useState(null);
    const [msg, setMsg] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const schoolId = localStorage.getItem("school_id");

    // -----------------------------
    // API URLs
    // -----------------------------
    const addURL = `/class-subjects/add`;
    const listURL = `/class-subjects/list`;
    const updateURL = `/class-subjects/update`;
    const deleteURL = `/class-subjects/delete`;

    // -----------------------------
    // Fetch dropdown data
    // -----------------------------
    useEffect(() => {
        // fetch classes
        fetch("https://dgps.co.in/api/get-classes")
            .then((res) => res.json())
            .then((data) => setClasses(data.data || []))
            .catch((error) => console.error("Error fetching class:", error));

        // fetch subjects
        fetchSubjects();
    }, []);

    const fetchSubjects = async () => {
        try {
            const res = await api.get(`subjects/list?school_id=${schoolId}`);
            setSubjects(res.data?.data || []);
        } catch (err) {
            console.error("Error fetching subjects", err);
        }
    };

    const fetchAssignments = async (classId) => {
        if (!classId) return;
        try {
            const res = await api.get(`${listURL}?school_id=${schoolId}&class_id=${classId}`);
            setAssignments(res.data?.data || []);
        } catch (err) {
            console.error("Error fetching assignments", err);
        }
    };

    // -----------------------------
    // Form handlers
    // -----------------------------
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMsg(null);

        if (!form.class_id || form.subject_ids.length === 0) {
            setMsg({ type: "error", text: "Class and subjects are required" });
            return;
        }

        try {
            let res;
            if (editId) {
                // update mode (single subject only)
                res = await api.post(updateURL, {
                    id: editId,
                    subject_id: form.subject_ids[0],
                });
            } else {
                // add mode (loop করে multiple subjects assign করবে)
                for (let subId of form.subject_ids) {
                    await api.post(addURL, {
                        class_id: form.class_id,
                        subject_id: subId,
                    });
                }
                res = { data: { status: true, message: "Subjects assigned successfully" } };
            }

            if (res.data?.status) {
                setMsg({ type: "success", text: res.data.message });
                setForm({ class_id: "", subject_ids: [] });
                setEditId(null);
                fetchAssignments(form.class_id);
            } else {
                setMsg({ type: "error", text: res.data.message || "Failed" });
            }
        } catch (err) {
            console.error("Save error", err);
            setMsg({ type: "error", text: err.response?.data?.message || "Network error" });
        }
    };

    const handleEdit = (item) => {
        setForm({ class_id: item.class_id, subject_ids: [item.subject_id] });
        setEditId(item.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this assignment?")) return;
        try {
            const res = await api.post(deleteURL, { id });
            if (res.data?.status) {
                setMsg({ type: "success", text: res.data.message });
                fetchAssignments(form.class_id);
            } else {
                setMsg({ type: "error", text: res.data.message });
            }
        } catch (err) {
            setMsg({ type: "error", text: err.message || "Network error" });
        }
    };

    // -----------------------------
    // UI
    // -----------------------------
    return (
        <div style={{ display: "flex", minHeight: "100vh" }}>
            <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
                <main style={{ padding: "30px", maxWidth: "1000px", margin: "auto", marginTop: "0px" }}>
                    {/* Add/Edit Assignment Section */}
                    <section style={cardStyle}>
                        <h2 style={titleStyle}>
                            {editId ? "✏️ Edit Assignment" : "➕ Assign Subjects to Class"}
                        </h2>

                        {msg && (
                            <div style={{ marginBottom: 12, color: msg.type === "error" ? "#cc0000" : "#166534" }}>
                                {msg.text}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div style={formGrid}>
                                {/* Select Class */}
                                <FormGroup label="Class">
                                    <Select
                                        options={classes.map((cls) => ({
                                            value: cls.id,
                                            label: cls.class_name,
                                        }))}
                                        value={
                                            form.class_id
                                                ? {
                                                    value: form.class_id,
                                                    label: classes.find((c) => c.id === form.class_id)?.class_name,
                                                }
                                                : null
                                        }
                                        onChange={(selected) => {
                                            setForm((prev) => ({ ...prev, class_id: selected ? selected.value : "" }));
                                            fetchAssignments(selected ? selected.value : null);
                                        }}
                                        placeholder="Search & select class..."
                                        isSearchable
                                        isClearable   // ✅ Clear button add করা হলো
                                    />
                                </FormGroup>

                                {/* Select Subjects */}
                                <FormGroup label="Subjects">
                                    <Select
                                        options={subjects.map((sub) => ({
                                            value: sub.id,
                                            label: sub.subject_name,
                                        }))}
                                        value={subjects
                                            .filter((sub) => form.subject_ids.includes(sub.id))
                                            .map((sub) => ({ value: sub.id, label: sub.subject_name }))}
                                        onChange={(selected) => {
                                            if (editId) {
                                                // edit mode -> single subject
                                                setForm((prev) => ({
                                                    ...prev,
                                                    subject_ids: selected ? [selected.value] : [],
                                                }));
                                            } else {
                                                // add mode -> multiple subjects
                                                setForm((prev) => ({
                                                    ...prev,
                                                    subject_ids: selected ? selected.map((s) => s.value) : [],
                                                }));
                                            }
                                        }}
                                        placeholder="Search & select subjects..."
                                        isMulti={!editId} // multiple select only in add mode
                                        isSearchable
                                    />
                                </FormGroup>
                            </div>

                            <div style={{ textAlign: "right", marginTop: "20px" }}>
                                <button type="submit" style={buttonStyle}>
                                    {editId ? "Update Assignment" : "Assign Subjects"}
                                </button>
                            </div>
                        </form>
                    </section>

                    {/* List Assignments */}
                    <section style={cardStyle}>
                        <h2 style={titleStyle}>📋 Assigned Subjects</h2>
                        {assignments.length === 0 ? (
                            <p>No subjects assigned for this class.</p>
                        ) : (
                            <ul style={{ listStyle: "none", padding: 0 }}>
                                {assignments.map((item, idx) => (
                                    <li
                                        key={item.id}
                                        style={{
                                            marginBottom: "10px",
                                            padding: "12px",
                                            borderRadius: "8px",
                                            background: idx % 2 === 0 ? "#f9f9f9" : "#eef3f9",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div>
                                            <strong>{item.subject_name}</strong>
                                        </div>
                                        <div>
                                            <FaEdit
                                                onClick={() => handleEdit(item)}
                                                style={{ color: "#0077cc", cursor: "pointer", marginRight: "12px" }}
                                            />
                                            <FaTrash
                                                onClick={() => handleDelete(item.id)}
                                                style={{ color: "red", cursor: "pointer" }}
                                            />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

// ---------------------------------
// Shared UI components & styles
// ---------------------------------
const formGrid = {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "20px",
};

const FormGroup = ({ label, children, style }) => (
    <div style={{ ...style }}>
        <label style={{ marginBottom: "6px", display: "block", fontWeight: 500, color: "#444" }}>
            {label}
        </label>
        {children}
    </div>
);

const buttonStyle = {
    padding: "10px 25px",
    backgroundColor: "#0077cc",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "15px",
    cursor: "pointer",
};

const cardStyle = {
    background: "#ffffff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
    marginBottom: "30px",
};

const titleStyle = {
    color: "#0077cc",
    marginBottom: "25px",
};

export default ClassSubjectAssignPage;
